import type { Order } from '@commercelayer/sdk'
import z from 'zod'

/**
 * Represents an Algolia product object inferred from a schema.
 * @typedef {Object} AlgoliaObject
 */
type AlgoliaObject = z.infer<typeof algoliaProductsSchema>

/**
 * Represents the type of action performed on the cart.
 * Used to track the last cart interaction for GA event dispatching.
 * It's possible to have more cart actions in the future, but currently we only track 'add' and 'increase' actions.
 * - 'add' - A new item was added to the cart
 * - 'increase' - An existing item's quantity was increased
 * @typedef {'add'} CartAction
 */
export type CartAction = 'add' | 'increase'

/**
 * Payload for cart-related Google Analytics events.
 * @typedef {Object} CartEventsPayload
 * @property {Order} [cart] - The current cart order.
 * @property {Array<AlgoliaObject | null>} [algoliaObjects] - Array of Algolia product objects.
 * @property {CartAction} [cartAction] - The last action performed on the cart. Undefined means no action has been attempted yet or it has been reset.
 */
type CartEventsPayload = {
  cart?: Order
  algoliaObjects?: Array<AlgoliaObject | null>
  cartAction?: CartAction
}

/**
 * Composable for managing Google Analytics ecommerce data.
 * Provides methods to retrieve and set cart events payload for analytics tracking.
 *
 * @returns {Object} An object with cart events payload management methods
 * @property {Ref<CartEventsPayload | null | undefined>} cartEventsPayload - Reactive cart events payload
 * @property {Function} getEcommerceData - Retrieves ecommerce data for analytics
 * @property {Function} setCartEventsPayload - Sets or updates cart events payload
 */
export const useGAEcommerceData = () => {
  const { mapData } = useMapGAEcommerceData()
  const cartEventsPayload = useGAEntity<CartEventsPayload | null | undefined>(
    'CART_GA_EVENTS_PAYLOAD'
  )

  /**
   * Returns the last tracked cart action stored for GA event dispatching.
   *
   * @returns {CartAction | undefined} The current cart action, if one has been recorded.
   */
  const getCartAction = () => {
    return cartEventsPayload.value?.cartAction
  }

  /**
   * Clears the stored cart payload used to build subsequent GA ecommerce events.
   */
  const resetCartEventsPayload = () => {
    cartEventsPayload.value = null
  }

  /**
   * Retrieves ecommerce data for Google Analytics tracking.
   *
   * @async
   * @param {number} [qty] - Optional quantity parameter
   * @returns {Promise<Object | undefined>} Mapped ecommerce data or undefined if no cart exists
   */
  const getEcommerceData = async (qty?: number) => {
    const cart = cartEventsPayload.value?.cart
    if (!cart || !cart?.line_items?.length) return
    return await mapData({
      cart,
      algoliaObjects: cartEventsPayload.value?.algoliaObjects ?? [],
      qty,
    })
  }

  const mergeAlgoliaObjectsIntoCartEventsPayload = (
    algoliaProduct: AlgoliaObject,
    onlyKeepCartProducts = false
  ) => {
    const existingObjects = cartEventsPayload.value?.algoliaObjects ?? []

    const cartObjectIDs = new Set(
      (cartEventsPayload.value?.cart?.line_items ?? [])
        .filter(item => item.sku_code)
        .map(item => getSkuWithoutSize(item.sku_code!))
    )

    const existingObjectsToKeep = onlyKeepCartProducts
      ? existingObjects.filter(item => item && cartObjectIDs.has(item.objectID))
      : existingObjects

    const allObjects = [...existingObjectsToKeep, algoliaProduct]

    const uniqueAlgoliaObjects = allObjects.reduce<AlgoliaObject[]>(
      (acc, item) => {
        if (!item) return acc
        const isDuplicate = acc.some(
          existing => existing.objectID === item.objectID
        )
        if (!isDuplicate) acc.push(item)
        return acc
      },
      []
    )

    setCartEventsPayload({ algoliaObjects: uniqueAlgoliaObjects })
  }

  /**
   * Sets or updates the cart events payload for Google Analytics.
   *
   * @param {CartEventsPayload} payload - The payload to set or update
   */
  const setCartEventsPayload = (payload: CartEventsPayload) => {
    cartEventsPayload.value = {
      ...cartEventsPayload.value,
      ...payload,
    }
  }

  return {
    getCartAction,
    getEcommerceData,
    setCartEventsPayload,
    mergeAlgoliaObjectsIntoCartEventsPayload,
    resetCartEventsPayload,
  }
}
